import { usePrevious } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { useDocumentRetrieve } from "gen/endpoints/documents/documents";
import { getHistoricalReportTableRetrieveQueryKey } from "gen/endpoints/reports/reports";
import { DocumentStateMachineFieldsEnum } from "gen/model/documentStateMachineFieldsEnum";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "shared/components/toast/useToast";
import { downloadFileFromUrl } from "shared/utils/utils";

const DOCUMENT_RETRIEVE_TOAST_ID = "document-retrieve";

const useDocumentPoll = (
  successMessage: string,
  errorMessage: string,
  startMessage: string,
) => {
  const { t } = useTranslation(["reports", "common"]);
  const { showToast, closeToast, toastIsActive, showErrorToast } = useToast();
  const [documentId, setDocumentId] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { data: documentData, isLoading: isDocumentLoading } =
    useDocumentRetrieve(documentId!, {
      query: {
        enabled: !!documentId,
        refetchInterval: 1000,
        throwOnError: false,
      },
    });

  const prevDocumentState = usePrevious(documentData?.state);

  useEffect(() => {
    if (
      documentData?.state === DocumentStateMachineFieldsEnum.upload_succeeded &&
      prevDocumentState !== DocumentStateMachineFieldsEnum.upload_succeeded
    ) {
      downloadFileFromUrl(documentData.url);
      showToast({
        variant: "success",
        message: successMessage,
      });
      void queryClient.invalidateQueries({
        queryKey: getHistoricalReportTableRetrieveQueryKey(),
      });
      setDocumentId(null);
    } else if (
      documentData?.state === DocumentStateMachineFieldsEnum.upload_failed &&
      prevDocumentState !== DocumentStateMachineFieldsEnum.upload_failed
    ) {
      showErrorToast(errorMessage);
      setDocumentId(null);
    }
  }, [
    documentData,
    prevDocumentState,
    queryClient,
    showErrorToast,
    showToast,
    successMessage,
    errorMessage,
    t,
  ]);

  useEffect(() => {
    if (
      documentId &&
      (isDocumentLoading ||
        documentData?.state === DocumentStateMachineFieldsEnum.no_upload)
    ) {
      if (!toastIsActive(DOCUMENT_RETRIEVE_TOAST_ID)) {
        showToast({
          id: DOCUMENT_RETRIEVE_TOAST_ID,
          message: startMessage,
          variant: "info",
          isClosable: false,
        });
      }
    } else {
      if (toastIsActive(DOCUMENT_RETRIEVE_TOAST_ID)) {
        closeToast(DOCUMENT_RETRIEVE_TOAST_ID);
      }
    }
  }, [
    documentData,
    closeToast,
    documentId,
    isDocumentLoading,
    showToast,
    t,
    toastIsActive,
    startMessage,
  ]);

  return { setDocumentId };
};

export default useDocumentPoll;
